import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Info, Car, Train, ShieldCheck } from "lucide-react";
import { HourlyWaitChart } from "./HourlyWaitChart";
import { QuickStats } from "./QuickStats";
import { getAirportDetails } from "@/data/airportDetails";
import { ProGate } from "@/components/ProGate";

interface AirportTabsProps {
  airportCode: string;
  airportName: string;
}

export const AirportTabs = ({ airportCode, airportName }: AirportTabsProps) => {
  const details = getAirportDetails(airportCode);

  return (
    <Tabs defaultValue="today" className="w-full">
      <TabsList className="w-full grid grid-cols-3 h-14 sm:h-12 gap-1 p-1">
        <TabsTrigger value="today" className="text-sm min-h-[48px] sm:min-h-[44px] rounded-lg">Today's Pattern</TabsTrigger>
        <TabsTrigger value="terminals" className="text-sm min-h-[48px] sm:min-h-[44px] rounded-lg">Terminals</TabsTrigger>
        <TabsTrigger value="tips" className="text-sm min-h-[48px] sm:min-h-[44px] rounded-lg">Insider Tips</TabsTrigger>
      </TabsList>

      <TabsContent value="today" className="space-y-6 mt-4">
        <Card>
          <CardContent className="pt-6">
            <ProGate feature="hourly_chart" fallback="blur">
              <HourlyWaitChart airportCode={airportCode} />
            </ProGate>
          </CardContent>
        </Card>

        <QuickStats airportCode={airportCode} />

        {details && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Car className="h-5 w-5 text-primary" />
                Parking Rates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Economy</p>
                  <p className="font-semibold">{details.parking.economy}</p>
                </div>
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Garage</p>
                  <p className="font-semibold">{details.parking.garage}</p>
                </div>
                {details.parking.valet && (
                  <div className="p-3 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Valet</p>
                    <p className="font-semibold">{details.parking.valet}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="terminals" className="space-y-6 mt-4">
        {details ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="h-5 w-5 text-primary" />
                {airportCode} Terminal Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">{details.terminals} Terminal{details.terminals > 1 ? "s" : ""}</h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  {details.terminalNames.map((terminal, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{terminal}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Hub Airlines</h4>
                <div className="flex flex-wrap gap-2">
                  {details.hubAirlines.map((airline, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{airline}</Badge>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Annual Passengers:</strong> {details.annualPassengers} million
                </p>
                <div className="flex flex-wrap gap-2">
                  {details.preCheckLanes && (
                    <Badge className="bg-success hover:bg-success/90 text-success-foreground gap-1">
                      <ShieldCheck className="h-3 w-3" /> TSA PreCheck
                    </Badge>
                  )}
                  {details.clearLanes && (
                    <Badge className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1">
                      CLEAR Lanes
                    </Badge>
                  )}
                </div>
              </div>

              {/* Transit */}
              <div className="pt-2 border-t border-border">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Train className="h-4 w-4" /> Getting to {airportCode}
                </h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {details.transitOptions.map((option, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {option}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Terminal details not yet available for {airportCode}.
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="tips" className="space-y-4 mt-4">
        {details ? (
          <>
            {/* Security Tips */}
            {details.tipsSecurity.map((tip, i) => (
              <div key={`sec-${i}`} className="flex gap-3 p-4 rounded-xl border border-primary/20 bg-card">
                <div className="flex-shrink-0 w-1 rounded-full bg-primary" />
                <div>
                  <Badge className="mb-1.5 text-[10px] bg-primary/10 text-primary hover:bg-primary/20 border-0">PRO TIP</Badge>
                  <p className="text-sm text-muted-foreground">{tip}</p>
                </div>
              </div>
            ))}

            {/* General Tips */}
            {details.tipsGeneral.map((tip, i) => (
              <div key={`gen-${i}`} className="flex gap-3 p-4 rounded-xl border border-border bg-card">
                <div className="flex-shrink-0 w-1 rounded-full bg-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">{tip}</p>
              </div>
            ))}
          </>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Tips not yet available for {airportCode}.
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );
};
